import React from 'react';
import ReactPlayer from 'react-player';
import { Button, Container, Row, Col, Badge } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BsPlayFill, BsPauseFill, BsSkipBackward, BsSkipForward } from 'react-icons/bs';
import BaseCourseContent from './BaseCourseContent';
import ForceLandscape, { czyUrzadzenieMobilne } from '../ForceLandscape';
import { trackTrainingEvent } from '../../trainingTelemetry';

// EKRAN KONCA MATERIALU ZAMYKA SIE SAM (Marcin, 2026-10-01): „w momencie kiedy nikt nic nie
// kliknie, to po trzynastu sekundach system automatycznie sam zachowa sie tak samo, jakbysmy
// kliknęli zakoncz szkolenie". Ekran nie moze zostac na stale (Fibonacci: 13 s).
const AUTO_DALEJ_S = 13;
// Przycisk „dalej" w dolnym pasku przy 85% materialu USUNIETY (Marcin, 2026-10-01): „koniec
// egzaminu pojawia sie na poziomie 80 paru procent, czyli przez kawalek wideo mozna skonczyc".
// Jedyne wyjscie z materialu to ekran konca (`lt-koniec-wideo`), niezaleznie od panelu przewijania.
// PANEL PRZEWIJANIA = USTAWIENIE SYSTEMU (Marcin, 2026-09-02: „przy testach sie przydaje, ale przy
// produkcyjnym uruchomieniu uzytkownik nie powinien miec mozliwosci przeskakiwania tresci, tylko
// powinien ja obejrzec w calosci"). Backend podaje `showSeekControls` w danych szkolenia
// (`training:show_seek_controls`, domyslnie WLACZONE). Brak pola = starszy backend = zachowanie
// sprzed tej zmiany, czyli panel widoczny.
const czyPrzewijanie = (props) => props.showSeekControls !== false;
// Ile czekamy na DOWOD, ze autoodtwarzanie ruszylo, zanim pokazemy kafel „dotknij, aby rozpoczac"
// (Fibonacci). Przegladarka nie mowi „zablokowalem" — po prostu nic sie nie dzieje, wiec jedynym
// sygnalem jest brak zdarzenia `onPlay`.
const CZAS_NA_AUTOSTART_MS = 1300;
// O ile sekund wolno wyprzedzic najdalszy OBEJRZANY moment, zanim uznamy to za przeskok.
// Natywny pasek wideo przewija skokowo i samo `timeupdate` potrafi wyprzedzic nasz licznik
// o ulamek sekundy — zerowa tolerancja szarpalaby odtwarzaniem przy zwyklym odsluchu.
const TOLERANCJA_PRZEWIJANIA_S = 1;
// Skok wykrywany z RAPORTOWANEGO POSTEPU (materialy w iframie — patrz `zablokujPrzeskok`).
// `onProgress` react-playera tyka co ~1 s, wiec normalne odtwarzanie przesuwa czas o okolo
// sekunde na tyknięcie; 3 s zostawia zapas na wolniejsze tyknięcie, a realny przeskok po pasku
// providera jest o rzad wielkosci wiekszy.
const TOLERANCJA_PRZESKOKU_S = 3;

/**
 * Component for handling video content from various providers (YouTube, Vimeo, Facebook)
 */
class VideoContent extends BaseCourseContent {
    static canHandle(url) {
        return ReactPlayer.canPlay(url);
    }

    state = {
        isVideoFinished: false,
        isPlaying: true,
        // `isPlaying` to NASZA prosba do odtwarzacza, a nie stan faktyczny. Telefon, ktory odmowil
        // autoodtwarzania z dzwiekiem, stoi zapauzowany, a przycisk pokazuje „pauza" — pierwsze
        // dotkniecie tylko zrownuje stan (nic nie widac), dopiero drugie odtwarza. Stad dwa
        // klikniecia zgloszone 2026-09-02. `hasStarted` niesie FAKT z odtwarzacza (`onPlay`).
        hasStarted: false,
        autoplayBlocked: false,
        playedSeconds: 0,
        totalSeconds: 0,
        played: 0,
        // Sekundy do automatycznego „dalej" na ekranie konca materialu (`AUTO_DALEJ_S`).
        sekundyDoDalej: AUTO_DALEJ_S,
        odliczanie: false,
        isSeekingLocked: false,
        hasSeekedToStart: false,
        // Telefon/tablet: natywny pasek wideo zamiast naszych przyciskow, pelny ekran i twarda
        // blokada przeskoku do przodu (Marcin, 2026-09-23 — prezentacja Werner Kenkel).
        mobilny: false,
        // Najdalszy moment, ktory kursant NAPRAWDE obejrzal. To jest sufit przewijania:
        // cofac wolno dowolnie, wyprzedzac — nie.
        maxObejrzaneSekundy: 0,
        // Czy pod spodem jest element `<video>` (plik), czy iframe providera (YouTube/Vimeo).
        // Od tego zalezy, CZY MAMY CO ODDAC natywnemu sterowaniu — w iframie nie mamy, wiec
        // wlasnego panelu nie wolno tam chowac (inaczej na telefonie nie da sie nawet zapauzowac).
        natywnyOdtwarzacz: false
    };

    playerRef = React.createRef();
    // Kadr materialu. Dla iframe (YouTube/Vimeo) NIE MA elementu `<video>`, wiec pelny ekran
    // mozna wziac tylko na kontenerze — patrz `wejdzWPelnyEkran` (finding kontroli
    // `[iframe-fullscreen-not-supported]`).
    scenaRef = React.createRef();

    // startSeconds/endSeconds (scenario-step fragment) optionally restrict the
    // playable range of the video. Undefined => whole video from 0, same as before
    // this was introduced.
    getFragmentBounds = (totalSeconds) => {
        const { startSeconds, endSeconds } = this.props;
        const total = totalSeconds || 0;
        const start = startSeconds && startSeconds > 0 ? startSeconds : 0;
        const end = endSeconds && endSeconds > 0 ? Math.min(endSeconds, total || endSeconds) : total;
        return { start, end: end > start ? end : start };
    };

    handleProgress = ({ playedSeconds, played }) => {
        // Budzet liczymy ZAWSZE, takze gdy bramka jest wylaczona — inaczej po wlaczeniu jej
        // w locie (np. wykryciu telefonu) pierwszy pomiar szedlby od nieaktualnego znacznika.
        const budzetPostepu = this.zanotujTykniecie();
        // Skok wykryty z postepu cofamy i NIE liczymy tego tyknięcia — inaczej sufit podniosl by
        // sie do wartosci, ktorej kursant nie obejrzal.
        if (this.zablokujPrzeskok(playedSeconds, budzetPostepu)) return;

        const { totalSeconds } = this.state;
        const { end } = this.getFragmentBounds(totalSeconds);
        const effectiveEnd = end > 0 ? end : totalSeconds;
        const reachedFragmentEnd = effectiveEnd > 0 && playedSeconds >= effectiveEnd;
        // KONIEC MATERIALU ODBLOKOWUJE EGZAMIN ZAWSZE — takze wtedy, gdy odtwarzacz zatrzyma sie
        // na raporcie 99% (zaokraglenie dlugosci) i `playedSeconds` nie dobije do `effectiveEnd`.
        // Bez tego przy wylaczonym przewijaniu (prog = 100%) material konczyl sie jako obejrzany,
        // a przycisk „rozpocznij egzamin" zostawal ukryty i uzytkownik nie mial jak isc dalej.
        // Koniec = mniej niz 1 s do konca (zaokraglenie dlugosci) albo `onEnded`. Prog 99% ucinal
        // ekranem konca ostatnie ~6 s dziesieciominutowego filmu (fallback CC !37, 🟠).
        const dobieglDoKonca = reachedFragmentEnd || (effectiveEnd > 0 && playedSeconds > effectiveEnd - 1);
        // Po „Obejrzyj ponownie" spozniony raport postepu sprzed przewiniecia nie moze wskrzesic
        // ekranu konca — czekamy, az odtwarzacz zamelduje pozycje sprzed konca.
        if (this.czekamNaPowrot) {
            if (dobieglDoKonca) return;
            this.czekamNaPowrot = false;
        }

        this.setState(stan => ({
            playedSeconds,
            played,
            // Sufit przewijania rosnie WYLACZNIE do przodu — cofniecie go nie obniza, inaczej
            // jedno cofniecie zabieralo by kursantowi prawo do przewiniecia z powrotem.
            maxObejrzaneSekundy: Math.max(stan.maxObejrzaneSekundy, playedSeconds),
            // Reset isVideoFinished if we're not near the end
            isVideoFinished: dobieglDoKonca ? stan.isVideoFinished : false
        }));

        if (dobieglDoKonca) {
            if (!this.state.isVideoFinished) {
                trackTrainingEvent('video_finished', 'video-finished', 'Video finished', {
                    url: contentUrl(this.props.content),
                    playedSeconds,
                    totalSeconds,
                });
                // Ekran konca zaslania kadr — natywny pelny ekran telefonu by go przykryl.
                this.wyjdzZPelnegoEkranu();
                this.setState({
                    isVideoFinished: true,
                    isPlaying: false // Stop playback when finished
                });
            }
        }
    };

    /**
     * KONIEC ODTWARZANIA — jedyne PEWNE zdarzenie „material obejrzany do konca".
     *
     * `onProgress` react-playera tyka co `progressInterval` (domyslnie 1000 ms), wiec `played`
     * rosnie skokami `1/D`. Przy krotkim materiale te skoki przeskakuja ponad prog `0.99`, a
     * `playedSeconds` nigdy nie dobija do `effectiveEnd`: ZMIERZONE dla D = 30 s — ostatnie
     * tykniecie pada na 29 s (`played` 0,967) i wideo sie konczy. Przy WYLACZONYM przewijaniu prog
     * przycisku wynosi 100%, wiec kursant zostawal z plakietka „97%" i nie mial jak przejsc dalej.
     * Kazdy material krotszy niz ~100 s wpada w to z definicji (`1/D > 0,01`).
     *
     * Ten haczyk domyka klase, a nie pojedynczy przypadek: nie podnosi progu ani nie zgaduje po
     * czasie, tylko sluchа odtwarzacza, ktory MOWI, ze skonczyl.
     */
    handleEnded = () => {
        if (!this.state.isVideoFinished) {
            trackTrainingEvent('video_finished', 'video-finished', 'Video finished', {
                url: contentUrl(this.props.content),
                playedSeconds: this.state.playedSeconds,
                totalSeconds: this.state.totalSeconds,
                zrodlo: 'onEnded',
            });
        }
        this.wyjdzZPelnegoEkranu();
        this.setState({ isVideoFinished: true, isPlaying: false });
    };

    handleDuration = (duration) => {
        this.setState({ totalSeconds: duration });
        const { start } = this.getFragmentBounds(duration);
        if (start > 0 && this.playerRef.current && !this.state.hasSeekedToStart) {
            this.playerRef.current.seekTo(start, 'seconds');
            // Sufit MUSI ruszyc od poczatku fragmentu: przeskok na `startSeconds` robi sam
            // odtwarzacz, a dla `zablokujPrzeskok` wygladalby jak proba obejscia materialu.
            this.setState(stan => ({
                hasSeekedToStart: true,
                playedSeconds: start,
                maxObejrzaneSekundy: Math.max(stan.maxObejrzaneSekundy, start),
            }));
        }
    };

    componentDidMount() {
        this.zmierzUrzadzenie();
        window.addEventListener('resize', this.zmierzUrzadzenie);
        window.addEventListener('orientationchange', this.zmierzUrzadzenie);
    }

    componentDidUpdate(prevProps, prevState) {
        // Inny material w tym samym egzemplarzu — ekran konca poprzedniego nie moze go przeskoczyc.
        if (contentUrl(prevProps.content) !== contentUrl(this.props.content) && this.state.isVideoFinished) {
            this.setState({ isVideoFinished: false, isPlaying: true });
            return;
        }
        if (!prevState.isVideoFinished && this.state.isVideoFinished) this.uruchomAutoDalej();
        if (prevState.isVideoFinished && !this.state.isVideoFinished) this.zatrzymajAutoDalej();
    }

    /** Odliczanie `AUTO_DALEJ_S` na ekranie konca; po zerze — to samo, co klikniecie „dalej". */
    uruchomAutoDalej = () => {
        this.zatrzymajAutoDalej();
        // Licznik na instancji, nie w `state`: React 18 laczy `setState` z timerow w paczki,
        // wiec odczyt `this.state` w kolejnym tyknięciu bywa nieaktualny.
        this.zostaloDoDalej = AUTO_DALEJ_S;
        this.setState({ sekundyDoDalej: AUTO_DALEJ_S, odliczanie: true });
        this.autoDalejTimer = setInterval(() => {
            this.zostaloDoDalej -= 1;
            if (this.zostaloDoDalej <= 0) {
                this.dalej('auto');
                return;
            }
            this.setState({ sekundyDoDalej: this.zostaloDoDalej });
        }, 1000);
    };

    zatrzymajAutoDalej = () => {
        if (this.autoDalejTimer) clearInterval(this.autoDalejTimer);
        this.autoDalejTimer = null;
    };

    /**
     * „Dalej" z ekranu konca — klikniete albo po odliczaniu. Zatrzymuje odliczanie, wiec timer
     * nie zdubluje klikniecia. Ponowne KLIKNIECIE jest dozwolone celowo: stanowisko po nieudanym
     * zapisie zaliczenia zostaje na tym ekranie i operator musi moc sprobowac jeszcze raz.
     */
    dalej = (zrodlo = 'klik') => {
        this.zatrzymajAutoDalej();
        // Odliczanie juz nie biegnie — napis „za 1 s" zamarzlby na ekranie (np. stanowisko po
        // bledzie zapisu zaliczenia). Bez automatycznego ponawiania: scenariusz bez pytan wyslalby
        // krok drugi raz, gdy serwer odpowiada dluzej niz 13 s.
        this.setState({ odliczanie: false });
        trackTrainingEvent('video_continue', 'video-continue', 'Video end screen continue', {
            url: contentUrl(this.props.content),
            zrodlo,
        });
        this.props.onExamStart();
    };

    componentWillUnmount() {
        this.zatrzymajAutoDalej();
        if (this.autostartTimer) clearTimeout(this.autostartTimer);
        window.removeEventListener('resize', this.zmierzUrzadzenie);
        window.removeEventListener('orientationchange', this.zmierzUrzadzenie);
        this.odepnijStraznikaPrzewijania();
    }

    zmierzUrzadzenie = () => {
        const mobilny = czyUrzadzenieMobilne();
        if (mobilny !== this.state.mobilny) this.setState({ mobilny });
    };

    /** Element `<video>` odtwarzacza — albo `null`, gdy materiał leci w iframie (YouTube/Vimeo). */
    elementWideo = () => {
        const odtwarzacz = this.playerRef.current;
        if (!odtwarzacz || typeof odtwarzacz.getInternalPlayer !== 'function') return null;
        const wewnetrzny = odtwarzacz.getInternalPlayer();
        // Sprawdzamy ZDOLNOSCI, nie klase: w tescie i w iframie `getInternalPlayer()` oddaje
        // obiekt bez `currentTime`, a `instanceof HTMLVideoElement` nie przezywa jsdom-a.
        if (!wewnetrzny || typeof wewnetrzny.addEventListener !== 'function') return null;
        if (typeof wewnetrzny.currentTime !== 'number') return null;
        return wewnetrzny;
    };

    /**
     * SUFIT PRZEWIJANIA NA NATYWNYM PASKU (Marcin, 2026-09-23: „pozwalamy użytkownikowi cofać
     * nagranie, ale nie przeskakiwać dalej do przodu — jak próbujemy do przodu, to jesteśmy
     * tylko w tym miejscu, do którego aktualnie doszliśmy").
     *
     * Bramka siedzi na zdarzeniu `seeking` SAMEGO elementu `<video>`, a nie na naszych
     * przyciskach — bo na telefonie sterowanie przejmuje natywny pasek (takze w pelnym ekranie,
     * gdzie nasz interfejs w ogole nie istnieje). Naszych przyciskow ta bramka nie dotyczy:
     * wlacza sie tylko tam, gdzie przewijanie w przod jest ZABRONIONE.
     */
    podepnijStraznikaPrzewijania = () => {
        const wideo = this.elementWideo();
        if (!wideo || this.wideoZeStraznikiem === wideo) return;
        this.odepnijStraznikaPrzewijania();
        wideo.addEventListener('seeking', this.pilnujPrzewijania);
        this.wideoZeStraznikiem = wideo;
    };

    odepnijStraznikaPrzewijania = () => {
        if (!this.wideoZeStraznikiem) return;
        this.wideoZeStraznikiem.removeEventListener('seeking', this.pilnujPrzewijania);
        this.wideoZeStraznikiem = null;
    };

    pilnujPrzewijania = () => {
        if (this.czyWolnoPrzewijacWPrzod()) return;
        const wideo = this.wideoZeStraznikiem;
        if (!wideo) return;
        const { maxObejrzaneSekundy } = this.state;
        // PROBE ZAPAMIETUJEMY PRZED PRZYCIECIEM (finding kontroli
        // `[seek-telemetry-records-rewound-time]`): `wideo.currentTime` po przypisaniu oddaje juz
        // sufit, wiec telemetria zapisywala „probe" rowna dozwolonemu maksimum i nie mowila nic
        // o tym, dokad kursant naprawde chcial przeskoczyc.
        const proba = wideo.currentTime;
        if (proba > maxObejrzaneSekundy + TOLERANCJA_PRZEWIJANIA_S) {
            wideo.currentTime = maxObejrzaneSekundy;
            trackTrainingEvent('video_seek_blocked', 'video-seek-blocked', 'Forward seek blocked', {
                url: contentUrl(this.props.content),
                proba,
                sufit: maxObejrzaneSekundy,
                zrodlo: 'seeking',
            });
        }
    };

    /**
     * STRAZNIK PRZEWIJANIA DLA MATERIALOW W IFRAME (finding kontroli
     * `[iframe-seek-guard-not-attached]`).
     *
     * YouTube i Vimeo nie daja elementu `<video>` — `elementWideo()` oddaje tam `null`, wiec
     * zdarzenie `seeking` nie ma sie na czym podpiac i sufit przewijania nie obowiazywal dla
     * materialow, ktore odtwarzacz obsluguje na rowni z plikami. Jedynym sygnalem, jaki daje
     * provider, jest SKOK w raportowanym postepie — i to on jest tu bramka.
     *
     * Dziala takze dla plikow: druga siec bezpieczenstwa nic nie psuje, bo regula jest ta sama.
     *
     * @returns {boolean} `true`, gdy skok zostal cofniety i biezacy postep nalezy pominac
     */
    zablokujPrzeskok = (playedSeconds, budzet = TOLERANCJA_PRZESKOKU_S) => {
        if (this.czyWolnoPrzewijacWPrzod()) return false;

        const sufit = this.state.maxObejrzaneSekundy;
        if (playedSeconds <= sufit + budzet) return false;

        const odtwarzacz = this.playerRef.current;
        if (!odtwarzacz || typeof odtwarzacz.seekTo !== 'function') return false;

        odtwarzacz.seekTo(sufit, 'seconds');
        trackTrainingEvent('video_seek_blocked', 'video-seek-blocked', 'Forward seek blocked', {
            url: contentUrl(this.props.content),
            proba: playedSeconds,
            sufit,
            zrodlo: 'onProgress',
        });
        this.setState({ playedSeconds: sufit });

        return true;
    };

    /**
     * ILE MATERIALU MOGLO UCZCIWIE PRZEJSC OD POPRZEDNIEGO TYKNIECIA (finding kontroli
     * `[progress-delay-treated-as-seek]`).
     *
     * Staly prog 3 s zakladal, ze `onProgress` tyka rowno co sekunde. Na telefonie tak nie jest:
     * przy uspionej karcie, zdjeciu watku glownego albo dlawieniu timerow kolejne tyknięcie
     * potrafi przyjsc po kilkunastu sekundach — i UCZCIWIE obejrzany odcinek wygladal wtedy jak
     * przeskok, wiec bramka cofala odtwarzacz w trakcie normalnego ogladania.
     *
     * Miara jest fizyczna: material nie przesunie sie dalej, niz uplynelo czasu na zegarze
     * (razy predkosc odtwarzania). Wszystko ponad to jest skokiem — i tego progu zadne
     * dlawienie timerow nie rozmiekcza.
     *
     * Efekt uboczny (przesuniecie znacznika) jest celowy — stad wolajacy ma to zrobic RAZ na
     * tyknięcie.
     *
     * @returns {number} dopuszczalne wyprzedzenie sufitu w sekundach
     */
    zanotujTykniecie = () => {
        const teraz = Date.now();
        const poprzednie = this.ostatnieTykniecieMs;
        this.ostatnieTykniecieMs = teraz;

        // Pauza nie posuwa materialu, wiec nie moze tez rozdawac budzetu — inaczej dlugie
        // zatrzymanie kupowalo kursantowi prawo do przeskoku o tyle samo sekund.
        if (!poprzednie || !this.state.isPlaying) return TOLERANCJA_PRZESKOKU_S;

        const uplyneloS = (teraz - poprzednie) / 1000;
        if (!(uplyneloS > 0)) return TOLERANCJA_PRZESKOKU_S;

        return TOLERANCJA_PRZESKOKU_S + uplyneloS * this.predkoscOdtwarzania();
    };

    /** Mnoznik predkosci odtwarzania; w iframie nieznany, wiec zakladamy 1x (nigdy mniej). */
    predkoscOdtwarzania = () => {
        const wideo = this.elementWideo();
        if (!wideo || typeof wideo.playbackRate !== 'number' || !(wideo.playbackRate > 0)) return 1;
        return Math.max(1, wideo.playbackRate);
    };

    /**
     * Na telefonie przewijanie w przod jest zabronione ZAWSZE — tam sterowanie oddajemy natywnemu
     * paskowi, wiec nie ma juz naszego panelu, ktorym mozna by je swiadomie wlaczyc. Na duzym
     * ekranie decyduja te same dwa przelaczniki co dotad (`showSeekControls` z ustawien systemu
     * i `allowSeekForward` ze stanowiska rejestracyjnego) — tam nic sie nie zmienia.
     */
    czyWolnoPrzewijacWPrzod = () => {
        const { allowSeekForward = true } = this.props;
        return !this.state.mobilny && czyPrzewijanie(this.props) && allowSeekForward;
    };

    /**
     * Pelny ekran na telefonie.
     *
     * DWIE DROGI, bo przegladarki daja dwie rozne (finding kontroli
     * `[iframe-fullscreen-not-supported]`):
     * - PLIK: element `<video>`. iOS zna WYLACZNIE `webkitEnterFullscreen` i wylacznie na `<video>`.
     * - IFRAME (YouTube/Vimeo): elementu `<video>` nie dostajemy w ogole, wiec pelny ekran bierzemy
     *   na KADRZE (`.lt-scena-wideo`) — iframe wypelnia go w 100%, wiec efekt jest ten sam.
     *   Na iPhonie `requestFullscreen` na zwyklym elemencie nie istnieje i przegladarka po prostu
     *   odmowi; wtedy zostaje wymuszony poziom z CSS, ktory i tak oddaje materialowi caly kadr.
     *   Lepiej sprobowac i dostac odmowe, niz nie sprobowac wcale.
     */
    wejdzWPelnyEkran = () => {
        if (!this.state.mobilny) return;
        const wideo = this.elementWideo();
        const cel = wideo || this.scenaRef.current;
        if (!cel) return;
        try {
            if (typeof cel.webkitEnterFullscreen === 'function') {
                cel.webkitEnterFullscreen();
            } else if (typeof cel.requestFullscreen === 'function') {
                const obietnica = cel.requestFullscreen();
                if (obietnica && typeof obietnica.catch === 'function') obietnica.catch(() => {});
            } else if (typeof cel.webkitRequestFullscreen === 'function') {
                cel.webkitRequestFullscreen();
            }
        } catch (blad) {
            // Przegladarka ma prawo odmowic (brak gestu uzytkownika). Szkolenie leci dalej
            // w kadrze — brak pelnego ekranu nie moze zatrzymac materialu.
        }
    };

    /** Po materiale wracamy do kadru, zeby decyzja („egzamin"/„ponownie") byla w ogole widoczna. */
    wyjdzZPelnegoEkranu = () => {
        const wideo = this.elementWideo();
        try {
            if (wideo && typeof wideo.webkitExitFullscreen === 'function') {
                wideo.webkitExitFullscreen();
            } else if (typeof document !== 'undefined'
                && document.fullscreenElement
                && typeof document.exitFullscreen === 'function') {
                const obietnica = document.exitFullscreen();
                if (obietnica && typeof obietnica.catch === 'function') obietnica.catch(() => {});
            }
        } catch (blad) {
            // jw. — brak wyjscia z pelnego ekranu nie moze wywrocic konca szkolenia.
        }
    };

    /** Odtwarzacz gotowy: dajemy autostartowi chwile i sprawdzamy, czy NAPRAWDE ruszyl. */
    handleReady = () => {
        this.rozpoznajOdtwarzacz();
        this.podepnijStraznikaPrzewijania();
        this.odliczAutostart();
    };

    /** Czy pod spodem jest `<video>` (plik), czy iframe providera — decyduje o panelu sterowania. */
    rozpoznajOdtwarzacz = () => {
        const natywny = null !== this.elementWideo();
        if (natywny !== this.state.natywnyOdtwarzacz) this.setState({ natywnyOdtwarzacz: natywny });
    };

    /**
     * BUFOROWANIE TO NIE BLOKADA. Staly odstep od `onReady` nie odroznia telefonu, ktory odmowil
     * autoodtwarzania, od wolnego lacza, ktore wlasnie sciaga pierwsze sekundy — a kafel „dotknij,
     * aby rozpoczac" przy dozwolonym autoplayu jest gorszy niz jego brak, bo sam zatrzymuje wideo.
     * Kazde `onBuffer` przesuwa wiec termin: liczymy ciszę odtwarzacza, nie czas od gotowosci.
     */
    handleBuffer = () => {
        if (this.autostartTimer) clearTimeout(this.autostartTimer);
    };

    handleBufferEnd = () => {
        if (!this.state.hasStarted) this.odliczAutostart();
    };

    odliczAutostart = () => {
        if (this.autostartTimer) clearTimeout(this.autostartTimer);
        this.autostartTimer = setTimeout(() => {
            if (!this.state.hasStarted) {
                trackTrainingEvent('video_autoplay_blocked', 'video-autoplay-blocked',
                    'Autoplay blocked by browser', { url: contentUrl(this.props.content) });
                this.setState({ autoplayBlocked: true, isPlaying: false });
            }
        }, CZAS_NA_AUTOSTART_MS);
    };

    /** FAKT z odtwarzacza — gasi kafel startowy i zrownuje stan przycisku z rzeczywistoscia. */
    handlePlay = () => {
        if (this.autostartTimer) clearTimeout(this.autostartTimer);
        const pierwszyStart = !this.state.hasStarted;
        this.rozpoznajOdtwarzacz();
        this.podepnijStraznikaPrzewijania();
        this.setState({ isPlaying: true, hasStarted: true, autoplayBlocked: false });
        // Gdy autoodtwarzanie przeszlo bez dotkniecia, to jest jedyny moment, w ktorym mozemy
        // jeszcze sprobowac pelnego ekranu. Odmowa przegladarki jest tu w porzadku.
        if (pierwszyStart) this.wejdzWPelnyEkran();
    };

    handlePause = () => {
        this.setState({ isPlaying: false });
    };

    /** Jedno dotkniecie kafla = start z dzwiekiem (gest uzytkownika, ktorego brakowalo). */
    handleStartTap = () => {
        trackTrainingEvent('video_tap_start', 'video-tap-start', 'Training started by tap', {
            url: contentUrl(this.props.content),
        });
        this.setState({ autoplayBlocked: false, isPlaying: true });
        // iOS wpuszcza w pelny ekran TYLKO w obrebie gestu uzytkownika — to jest ten moment.
        this.wejdzWPelnyEkran();
    };

    /**
     * „Obejrzyj ponownie" — wraca na poczatek materialu (albo fragmentu scenariusza) i odtwarza.
     * Sufit przewijania ZOSTAJE na osiagnietej wartosci: kto obejrzal cale wideo, moze potem
     * skakac po nim swobodnie, bo nie ma juz czego obejsc.
     */
    obejrzyjPonownie = () => {
        const { start } = this.getFragmentBounds(this.state.totalSeconds);
        trackTrainingEvent('video_replay', 'video-replay', 'Video replay', {
            url: contentUrl(this.props.content),
        });
        if (this.playerRef.current && typeof this.playerRef.current.seekTo === 'function') {
            this.playerRef.current.seekTo(start, 'seconds');
        }
        this.czekamNaPowrot = true;
        this.setState({ isVideoFinished: false, isPlaying: true, playedSeconds: start });
    };

    handlePlayPause = () => {
        trackTrainingEvent('video_toggle', 'video-toggle', 'Video play/pause toggled', {
            url: contentUrl(this.props.content),
            wasPlaying: this.state.isPlaying,
            playedSeconds: this.state.playedSeconds,
        });
        this.setState(state => ({ isPlaying: !state.isPlaying }));
    };

    handleSeek = async (seconds, direction = 'forward') => {
        const { isSeekingLocked, playedSeconds, totalSeconds } = this.state;
        const { start, end } = this.getFragmentBounds(totalSeconds);
        const lowerBound = start;
        const upperBound = (end > 0 ? end : totalSeconds) - 5;

        if (isSeekingLocked) return;

        this.setState({ isSeekingLocked: true });

        if (this.playerRef.current) {
            const newTime = direction === 'forward'
                ? Math.min(playedSeconds + seconds, upperBound)
                : Math.max(playedSeconds - seconds, lowerBound);

            try {
                await this.playerRef.current.seekTo(newTime, 'seconds');
                trackTrainingEvent('video_seek', 'video-seek', 'Video seek', {
                    url: contentUrl(this.props.content),
                    direction,
                    seconds,
                    fromSeconds: playedSeconds,
                    toSeconds: newTime,
                });
            } catch (error) {
                console.error('Seeking failed:', error);
            }

            // Add a small delay before unlocking to prevent rapid clicking
            setTimeout(() => {
                this.setState({ isSeekingLocked: false });
            }, 300);
        }
    };

    handleSkipBackward = (seconds = 10) => {
        this.handleSeek(seconds, 'backward');
    };

    handleSkipForward = (seconds = 10) => {
        this.handleSeek(seconds, 'forward');
    };

    getPlayerConfig = (url) => {
        const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
        const isVimeo = url.includes('vimeo.com');
        const isFacebook = url.includes('facebook.com');

        const config = {};

        if (isYouTube) {
            config.youtube = {
                playerVars: { 
                    modestbranding: 1, // Hide YouTube logo
                    controls: 0, // Disable native controls
                    disablekb: 1, // Disable keyboard controls
                    rel: 0, // Disable related videos
                    showinfo: 0, // Hide video information
                    iv_load_policy: 3, // Disable annotations
                    autoplay: 1, // Enable autoplay
                    fs: 0, // Disable fullscreen button
                    playsinline: 1, // Force playback within iframe
                    origin: window.location.origin, // Security: prevent redirects
                    playlist: url.split('v=')[1], // Required to stop on last frame
                    endSeconds: 0.5 // Stop slightly before the end to prevent suggestions
                }
            };
        }

        if (isVimeo) {
            config.vimeo = {
                playerOptions: {
                    controls: false, // Disable native controls
                    autoplay: true, // Enable autoplay
                    dnt: true, // Disable tracking
                    playsinline: true, // Force playback within iframe
                    portrait: false, // Hide author avatar
                    title: '', // Hide video title (empty string instead of false)
                    byline: '', // Hide author name (empty string instead of false)
                    background: false, // Disable background mode to prevent muting
                    pip: false, // Disable picture-in-picture
                    keyboard: false, // Disable keyboard controls
                    transparent: false, // Disable transparent background
                    autopause: false, // Disable auto-pause
                    texttrack: false, // Disable text tracks
                    quality: 'auto', // Set quality to auto
                    muted: false, // Ensure video is not muted
                    volume: 1 // Set volume to maximum
                }
            };
        }

        if (isFacebook) {
            config.facebook = {
                appId: process.env.REACT_APP_FACEBOOK_APP_ID,
                controls: false, // Disable native controls
                autoplay: true // Enable autoplay
            };
        }

        return config;
    };

    render() {
        // `continueLabelKey` (Marcin, Task 11b/11c): etykieta ostatniego materialu domyslnie
        // zapowiada egzamin (`start_exam`) — poprawnie w trybie logowania pracownika, gdzie po
        // materiale ZAWSZE jest test. Stanowisko rejestracyjne (RegistrationStation.js) nigdy nie
        // ma egzaminu — nadzor zastepuje ankiete z definicji trybu — wiec podaje WLASNY klucz
        // (`finish_training_title`, "Zakoncz szkolenie"). Domyslna wartosc zostaje `start_exam`,
        // zeby istniejaca sciezka (Training.js) nie zmienila zachowania ani o jeden znak.
        // `allowSeekForward` (Marcin, 2026-09-17 — zgloszenie Dawida Blachowskiego): stanowisko
        // rejestracyjne (RegistrationStation.js) zastepuje ankiete fizycznym nadzorem — mozliwosc
        // przeskoczenia materialu DO PRZODU uniewazniałaby to zalozenie (i nadzor, i material).
        // Cofanie NIE unieważnia niczego (mozna tylko dosluchac czegos ponownie), wiec zostaje.
        // Domyslnie `true`, zeby Training.js (tryb logowania pracownika) nie zmienil sie ani o
        // jeden przycisk — to WYLACZNIE stanowisko podaje `false`.
        const { content, t, isLastMaterial, continueLabelKey, allowSeekForward = true } = this.props;
        const { isVideoFinished, isPlaying, sekundyDoDalej, odliczanie, played, playedSeconds, totalSeconds, isSeekingLocked, autoplayBlocked, mobilny, natywnyOdtwarzacz } = this.state;
        // NA TELEFONIE STERUJE NATYWNY PASEK WIDEO (Marcin, 2026-09-23): „te przyciski pod wideo
        // są dobre w kontekście komputera, ale w kontekście telefonu (...) ich nie widać, trzeba
        // przesunąć ekran w dół, a w trybie pełnoekranowym w ogóle nie ma do nich dostępu".
        // Nasz panel zostaje wiec WYLACZNIE na duzym ekranie, a na telefonie `controls` oddaje
        // pasek przegladarki (z przewijaniem i przyciskiem pelnego ekranu), spiety z blokada
        // przeskoku do przodu (`pilnujPrzewijania`).
        // 🔴 Poprawka po kontroli (`[iframe-seek-guard-not-attached]`, druga polowa tego samego
        // problemu): panel wolno schowac TYLKO wtedy, gdy jest co oddac — czyli gdy pod spodem
        // jest element `<video>` z natywnym paskiem. Dla YouTube/Vimeo `getPlayerConfig()` wylacza
        // sterowanie providera (`controls: 0`), wiec ukrycie naszego panelu zostawialoby kursanta
        // na telefonie BEZ MOZLIWOSCI ZAPAUZOWANIA materialu.
        const natywneSterowanie = mobilny && natywnyOdtwarzacz;
        const naszeSterowanie = !natywneSterowanie;
        const pokazPrzewijanie = czyPrzewijanie(this.props) && naszeSterowanie;
        // `&& !mobilny` — JEDNA regula z `czyWolnoPrzewijacWPrzod()`: na telefonie do przodu sie
        // nie przewija, takze przy materiale w iframie, gdzie nasz panel zostaje widoczny.
        // Bez tego przycisk „+10 s" pokazywalby sie na telefonie, a straznik natychmiast cofalby
        // jego dzialanie — interfejs klamalby o tym, co wolno.
        const pokazPrzewijanieWPrzod = pokazPrzewijanie && allowSeekForward && !mobilny;
        // "Next" while more materials follow, `continueLabelKey` (default "Start exam") on the last one.
        const continueLabel = isLastMaterial === false ? t('next_material') : t(continueLabelKey || 'start_exam');
        // Fragment-relative progress (falls back to whole-video `played` when no
        // startSeconds/endSeconds bounds are set, i.e. today's unchanged behaviour).
        const { start: fragStart, end: fragEnd } = this.getFragmentBounds(totalSeconds);
        const fragDuration = fragEnd - fragStart;
        const fragmentPlayed = fragDuration > 0
            ? Math.min(1, Math.max(0, (playedSeconds - fragStart) / fragDuration))
            : played;

        if (!content || !content.url) {
            return <div>{t('video_not_available')}</div>;
        }

        return (
            <ForceLandscape>
            <Container fluid className="VideoContent h-100 d-flex flex-column p-0 position-relative">
                {isVideoFinished && (
                    /* EKRAN KONCA MATERIALU (Marcin, 2026-10-01): wideo ZNIKA — zamiast przyciemnionego
                       ostatniego kadru („Dziekujemy" z filmu w tle) widac tlo zakladu, jak na reszcie
                       reaktora. „Dalej" 62% szerokosci, „Obejrzyj ponownie" 38% (zloty podzial).
                       Odtwarzacz zostaje zamontowany POD spodem, zeby „ponownie" mialo co przewinac. */
                    <div className="lt-koniec-wideo" data-testid="koniec-wideo" role="dialog" aria-modal="true" aria-label={t('video_finished_title')}>
                        <span className="lt-koniec-wideo-tytul">{t('video_finished_title')}</span>
                        <div className="lt-koniec-wideo-przyciski">
                            <Button
                                variant="danger"
                                size="lg"
                                className="lt-koniec-wideo-dalej"
                                autoFocus
                                onClick={() => this.dalej('klik')}
                                data-testid="koniec-wideo-dalej"
                            >
                                {continueLabel}
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="lg"
                                className="lt-koniec-wideo-ponownie"
                                onClick={this.obejrzyjPonownie}
                                data-testid="koniec-wideo-ponownie"
                            >
                                {t('watch_again')}
                            </Button>
                        </div>
                        {odliczanie && (
                            <span className="lt-koniec-wideo-odliczanie" data-testid="koniec-wideo-odliczanie" aria-live="polite">
                                {t('auto_continue_in', { seconds: sekundyDoDalej })}
                            </span>
                        )}
                    </div>
                )}
                {/* g-0: kontener ma p-0, wiec ujemne marginesy .row nie maja czym sie
                    skompensowac i caly wiersz wystaje 12 px poza ekran (zmierzone na 390 px). */}
                <Row className="flex-grow-1 mb-0 bg-transparent g-0 lt-wiersz-wideo" aria-hidden={isVideoFinished || undefined} inert={isVideoFinished ? '' : undefined}>
                    <Col className="d-flex align-items-center justify-content-center">
                        {/* W wymuszonym poziomie scena liczy sie od WYSOKOSCI kadru — patrz `.lt-kadr-poziomy
                            .lt-scena-wideo` w App.css. Zmierzone na 390x844: 16:9 z pelnej szerokosci
                            (844 px) daje 475 px wysokosci i wypycha pasek sterowania poza ekran. */}
                        <div className="lt-scena-wideo" ref={this.scenaRef}>
                            {autoplayBlocked && (
                                // Przegladarka telefonu nie pozwala zaczac wideo z dzwiekiem bez
                                // dotkniecia. Zamiast szukania malego przycisku „play" — CALY kadr
                                // jest przyciskiem: jedno dotkniecie i szkolenie leci.
                                <button
                                    type="button"
                                    data-testid="kafel-start"
                                    onClick={this.handleStartTap}
                                    className="border-0 d-flex flex-column align-items-center justify-content-center"
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 2,
                                        background: 'rgba(0,0,0,0.72)',
                                        color: '#fff',
                                        gap: '0.75rem',
                                    }}
                                >
                                    <BsPlayFill size={64} />
                                    <span className="fs-5">{t('tap_to_start')}</span>
                                </button>
                            )}
                            <ReactPlayer
                                ref={this.playerRef}
                                url={content.url}
                                playing={isPlaying}
                                width="100%"
                                height="100%"
                                onProgress={this.handleProgress}
                                onDuration={this.handleDuration}
                                onReady={this.handleReady}
                                onPlay={this.handlePlay}
                                onPause={this.handlePause}
                                onEnded={this.handleEnded}
                                onBuffer={this.handleBuffer}
                                onBufferEnd={this.handleBufferEnd}
                                controls={natywneSterowanie}
                                playsinline={!natywneSterowanie}
                                config={this.getPlayerConfig(content.url)}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="bg-light py-2 mt-auto g-0" aria-hidden={isVideoFinished || undefined} inert={isVideoFinished ? '' : undefined}>
                    {/* flex-wrap + gap: na telefonie piec przyciskow sterowania i plakietka
                        postepu nie miescily sie w jednym wierszu i wychodzily poza kadr. */}
                    <Col className="d-flex flex-wrap justify-content-center align-items-center gap-2 px-2">
                        {pokazPrzewijanie && (
                            <>
                                <Button 
                                    variant="outline-danger" 
                                    onClick={() => this.handleSkipBackward(60)}
                                    disabled={isSeekingLocked}
                                    data-testid="video-skip-backward-60"
                                >
                                    <BsSkipBackward /> 60s
                                </Button>
                                <Button 
                                    variant="outline-danger" 
                                    onClick={() => this.handleSkipBackward(10)}
                                    disabled={isSeekingLocked}
                                    data-testid="video-skip-backward-10"
                                >
                                    <BsSkipBackward /> 10s
                                </Button>
                            </>
                        )}
                        {naszeSterowanie && (
                            <Button
                                variant="danger"
                                onClick={this.handlePlayPause}
                            >
                                {isPlaying ? <BsPauseFill size={24} /> : <BsPlayFill size={24} />}
                            </Button>
                        )}
                        {pokazPrzewijanieWPrzod && (
                            <>
                                <Button
                                    variant="outline-danger"
                                    onClick={() => this.handleSkipForward(10)}
                                    disabled={isSeekingLocked}
                                    data-testid="video-skip-forward-10"
                                >
                                    10s <BsSkipForward />
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    onClick={() => this.handleSkipForward(60)}
                                    disabled={isSeekingLocked}
                                    data-testid="video-skip-forward-60"
                                >
                                    60s <BsSkipForward />
                                </Button>
                            </>
                        )}
                        <Badge bg="secondary">
                            {t('video_progress', { percent: Math.round(fragmentPlayed * 100) })}
                        </Badge>
                    </Col>
                </Row>
            </Container>
            </ForceLandscape>
        );
    }
}

function contentUrl(content) {
    return content && content.url ? content.url : null;
}

export default withTranslation()(VideoContent);
